import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../config/api';
import toast from 'react-hot-toast';
import SEO from '../components/SEO';
import { 
    EnvelopeIcon, 
    MapPinIcon, 
    PhoneIcon,
    PaperAirplaneIcon
} from '@heroicons/react/24/outline';

const ContactPage = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: '',
        message: ''
    });
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);

    const fadeInUp = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0 }
    };

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const response = await axios.post(`${API_BASE_URL}/api/contact`, formData);
            
            toast.success(response.data.message || 'Message sent successfully!');
            setSubmitted(true);
            
            // Reset form after 3 seconds
            setTimeout(() => {
                setSubmitted(false);
                setFormData({
                    name: '',
                    email: '',
                    subject: '',
                    message: ''
                });
            }, 3000);
        } catch (error) {
            console.error('Contact form error:', error);
            toast.error(error.response?.data?.message || 'Failed to send message. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <SEO 
                title="Contact Us | AircoreDynamics"
                description="Get in touch with AircoreDynamics team. We're here to help with your questions about aviation courses, aerospace content, and platform support."
                keywords="contact us, support, help, get in touch, aviation support"
                image="https://aircoredynamics.com/og-image.png"
                url="/contact"
                type="website"
            />
            <motion.div
                initial="hidden"
                animate="visible"
                variants={fadeInUp}
                transition={{ duration: 0.6 }}
                className="container mx-auto px-6 py-12 max-w-6xl"
            >
            {/* Header */}
            <div className="text-center mb-12">
                <EnvelopeIcon className="h-16 w-16 mx-auto mb-4 text-light-primary dark:text-dark-primary" />
                <h1 className="text-4xl md:text-5xl font-bold mb-4">
                    Contact <span className="gradient-text">Us</span>
                </h1>
                <p className="text-light-text/70 dark:text-dark-text/70 max-w-2xl mx-auto">
                    Have a question or feedback? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                
                {/* Contact Information */}
                <div className="space-y-6">
                    <motion.div
                        variants={fadeInUp}
                        transition={{ delay: 0.2 }}
                        className="card p-6"
                    >
                        <div className="flex items-start gap-4">
                            <div className="p-3 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-500">
                                <EnvelopeIcon className="h-6 w-6 text-white" />
                            </div>
                            <div>
                                <h3 className="font-bold text-lg mb-2">Email</h3>
                                <a 
                                    href="mailto:info@aircoredynamics.com"
                                    className="text-light-text/70 dark:text-dark-text/70 hover:text-light-primary dark:hover:text-dark-primary transition-colors"
                                >
                                    info@aircoredynamics.com
                                </a>
                                <br />
                                <a 
                                    href="mailto:support@aircoredynamics.com"
                                    className="text-light-text/70 dark:text-dark-text/70 hover:text-light-primary dark:hover:text-dark-primary transition-colors"
                                >
                                    support@aircoredynamics.com
                                </a>
                            </div>
                        </div>
                    </motion.div>

                    <motion.div
                        variants={fadeInUp}
                        transition={{ delay: 0.3 }}
                        className="card p-6"
                    >
                        <div className="flex items-start gap-4">
                            <div className="p-3 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500">
                                <MapPinIcon className="h-6 w-6 text-white" />
                            </div>
                            <div>
                                <h3 className="font-bold text-lg mb-2">Location</h3>
                                <p className="text-light-text/70 dark:text-dark-text/70">
                                    Worldwide<br />
                                    Remote Learning Platform
                                </p>
                            </div>
                        </div>
                    </motion.div>

                    <motion.div
                        variants={fadeInUp}
                        transition={{ delay: 0.4 }}
                        className="card p-6"
                    >
                        <div className="flex items-start gap-4">
                            <div className="p-3 rounded-lg bg-gradient-to-br from-green-500 to-emerald-500">
                                <PhoneIcon className="h-6 w-6 text-white" />
                            </div>
                            <div>
                                <h3 className="font-bold text-lg mb-2">Support Hours</h3>
                                <p className="text-light-text/70 dark:text-dark-text/70">
                                    Monday - Friday<br />
                                    9:00 AM - 6:00 PM (GMT+3)
                                </p>
                            </div>
                        </div>
                    </motion.div>

                    {/* Social Media */}
                    <motion.div
                        variants={fadeInUp}
                        transition={{ delay: 0.5 }}
                        className="card p-6"
                    >
                        <h3 className="font-bold text-lg mb-4">Follow Us</h3>
                        <div className="flex gap-3">
                            <a 
                                href="https://instagram.com/aircoredynamics" 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="p-3 rounded-lg bg-light-bg dark:bg-dark-bg hover:bg-gradient-to-br hover:from-purple-500 hover:to-pink-500 hover:text-white transition-all"
                            >
                                <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
                                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.85s-.011 3.584-.069 4.85c-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07s-3.584-.012-4.85-.07c-3.252-.148-4.771-1.691-4.919-4.919-.058-1.265-.069-1.645-.069-4.85s.011-3.584.069-4.85c.149-3.225 1.664-4.771 4.919-4.919C8.416 2.175 8.796 2.163 12 2.163zm0 1.44s-3.442.01-4.693.062c-2.61.12-3.774 1.264-3.894 3.894C3.35 8.78 3.36 9.16 3.36 12s-.01 3.22.053 4.458c.12 2.63 1.284 3.774 3.894 3.894 1.25.053 4.693.062 4.693.062s3.442-.01 4.693-.062c2.61-.12 3.774-1.264 3.894-3.894.062-1.25.053-4.693.053-4.693s.01-3.442-.053-4.693c-.12-2.63-1.284-3.774-3.894-3.894C15.442 3.613 12 3.603 12 3.603zm0 4.268c-2.404 0-4.355 1.95-4.355 4.355s1.95 4.355 4.355 4.355 4.355-1.95 4.355-4.355S14.404 7.87 12 7.87zm0 7.27c-1.608 0-2.915-1.307-2.915-2.915S10.392 9.32 12 9.32s2.915 1.307 2.915 2.915-1.307 2.915-2.915 2.915zm4.805-7.87c-.665 0-1.203.538-1.203 1.203s.538 1.203 1.203 1.203 1.203-.538 1.203-1.203-.538-1.203-1.203-1.203z"></path>
                                </svg>
                            </a>
                            <a 
                                href="https://x.com/aircoredynamics" 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="p-3 rounded-lg bg-light-bg dark:bg-dark-bg hover:bg-gradient-to-br hover:from-blue-500 hover:to-cyan-500 hover:text-white transition-all"
                            >
                                <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
                                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"></path>
                                </svg>
                            </a>
                            <a 
                                href="https://linkedin.com/company/aircoredynamics" 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="p-3 rounded-lg bg-light-bg dark:bg-dark-bg hover:bg-gradient-to-br hover:from-blue-600 hover:to-blue-700 hover:text-white transition-all"
                            >
                                <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
                                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"></path>
                                </svg>
                            </a>
                        </div>
                    </motion.div>
                </div>

                {/* Contact Form */}
                <motion.div
                    variants={fadeInUp}
                    transition={{ delay: 0.3 }}
                    className="lg:col-span-2"
                >
                    <div className="card p-8">
                        <h2 className="text-2xl font-bold mb-6">Send us a Message</h2>
                        
                        {submitted ? (
                            <div className="text-center py-12">
                                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-500/20 mb-4">
                                    <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                </div>
                                <h3 className="text-xl font-bold mb-2">Message Sent!</h3>
                                <p className="text-light-text/70 dark:text-dark-text/70">
                                    Thank you for contacting us. We'll get back to you soon.
                                </p>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-semibold mb-2" htmlFor="name">
                                            Name
                                        </label>
                                        <input
                                            type="text"
                                            id="name"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            required
                                            className="w-full px-4 py-3 rounded-lg bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border focus:border-light-primary dark:focus:border-dark-primary outline-none transition-colors"
                                            placeholder="Your name"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold mb-2" htmlFor="email">
                                            Email
                                        </label>
                                        <input
                                            type="email"
                                            id="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            required
                                            className="w-full px-4 py-3 rounded-lg bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border focus:border-light-primary dark:focus:border-dark-primary outline-none transition-colors"
                                            placeholder="your.email@example.com"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold mb-2" htmlFor="subject">
                                        Subject
                                    </label>
                                    <input
                                        type="text"
                                        id="subject"
                                        name="subject"
                                        value={formData.subject}
                                        onChange={handleChange}
                                        required
                                        className="w-full px-4 py-3 rounded-lg bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border focus:border-light-primary dark:focus:border-dark-primary outline-none transition-colors"
                                        placeholder="What is this about?"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold mb-2" htmlFor="message">
                                        Message
                                    </label>
                                    <textarea
                                        id="message"
                                        name="message"
                                        value={formData.message}
                                        onChange={handleChange}
                                        required
                                        rows="6"
                                        className="w-full px-4 py-3 rounded-lg bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border focus:border-light-primary dark:focus:border-dark-primary outline-none transition-colors resize-none"
                                        placeholder="Your message..."
                                    />
                                </div>

                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="btn-primary w-full group disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {loading ? (
                                        <>
                                            <span className="inline-block animate-spin mr-2">⏳</span>
                                            Sending...
                                        </>
                                    ) : (
                                        <>
                                            Send Message
                                            <PaperAirplaneIcon className="inline-block ml-2 h-5 w-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                                        </>
                                    )}
                                </button>
                            </form>
                        )}
                    </div>
                </motion.div>
            </div>

            {/* Back Button */}
            <div className="text-center mt-8">
                <Link 
                    to="/" 
                    className="btn-secondary inline-block"
                >
                    Back to Home
                </Link>
            </div>
        </motion.div>
        </>
    );
};

export default ContactPage;
